import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { nameIdPairValidationSchema, truncateString } from 'utils/general';
import * as yup from 'yup';

import { CreateTechnicalOperation } from '@/modules/workingDay';
import { OperationCategory } from '@/modules/workingDay/components/CreateTechnicalOperation/OperationCategory';
import { Review } from '@/modules/workingDay/components/CreateTechnicalOperation/Review';
import { TargetSetting } from '@/modules/workingDay/components/CreateTechnicalOperation/TargetSetting';
import { IWorkingDay } from '@/modules/workingDay/types';
import { CircularBgImage, Typography } from '@/shared/components/atoms';
import ValidateFieldModal from '@/shared/components/atoms/Demo/Modals/CreateFieldFirst';
import { IIconWithTitleDescriptionProps } from '@/shared/components/atoms/IconWithTItleDescription/IconWithTitleDescription';
import MiniComponentContainer from '@/shared/components/atoms/MiniComponentContainer';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { IMultiStepFormStep } from '@/shared/components/molecules/MultiStepForm/IMultiStepForm';
import { roles } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import useCheckPropertyFields from '@/shared/hooks/useCheckPropertyFields';

import CalendarIcon from '~/images/shared/calender.svg';
import ClipboardIcon from '~/images/shared/Clipboard.svg';
import ClockIcon from '~/images/shared/clock.svg';
import LocationIcon from '~/images/shared/Location.svg';
import TargetIcon from '~/images/shared/NoOutlineTarget.svg';
import ProfileIcon from '~/images/shared/Profile.svg';
import ScissorsIcon from '~/images/shared/Scissors.svg';

export const useCreateTechnicalOperationUtils = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { openModal } = useModal();
  const { hasFields } = useCheckPropertyFields();

  const isProductionOrFinanceManager =
    user?.userGroupId === roles.PRODUCTION_MANAGER ||
    user?.userGroupId === roles.FINANCIAL_MANAGER;

  const openCreateTechnicalOperationModal = () => {
    if (!hasFields || (isProductionOrFinanceManager && hasFields))
      openModal(<ValidateFieldModal />);
    else
      openModal(<CreateTechnicalOperation />, {
        variant: 'full',
        title: (
          <Typography variant='h2'>
            {t('create-technical-operation')}
          </Typography>
        ),
        onClose: () => {
          openModal(
            <ConfirmationModal
              header={t('going-somewhere-else')}
              message={t('data-loss-warning')}
              variant='warning'
              dismissText={t('continue-editing')}
              confirmText={t('discard-and-exit')}
              onConfirmRemoveNumber={2}
              buttonsAlignment='col'
              preferredAction='dismiss'
            />
          );
        },
      });
  };

  const getFormSteps =
    (): IMultiStepFormStep<IWorkingDay.IOperationFormValuesSchema>[] => [
      {
        title: t('operation-details'),
        childrenTitle: t('operation-details'),
        render: () => <OperationCategory />,
        fields: [
          'assignee.id',
          'entityId',
          'field.id',
          'startDate',
          'job.jobId',
        ],
      },
      {
        title: t('target-setting'),
        render: () => <TargetSetting />,
        fields: [
          'measurementUnit.id',
          'targetDuration.id',
          'averageTargetPerDay',
          'note',
        ],
      },
      {
        title: t('review'),
        render: ({ setStep }) => <Review setStep={setStep} />,
        fields: [],
      },
    ];

  const getFormValues = (
    values: IWorkingDay.IOperationFormValuesSchema
  ): IWorkingDay.IOperationFormSchema => {
    return {
      assignee: values.assignee.id,
      fieldId: values.field.id,
      jobId: values.job.jobId,
      averageTargetPerDay: values.averageTargetPerDay,
      measurementUnitId: values.measurementUnit.id,
      startDate: format(values.startDate, 'yyyy-MM-dd'),
      targetDurationId: values.targetDuration.id,
      entityId: values.entityId,
      note: values.note,
      recurring: values.recurring,
    };
  };

  const getGeneralCardItems = (
    formValues: IWorkingDay.IOperationFormValuesSchema
  ): IIconWithTitleDescriptionProps[] => [
    {
      title: t('assignee'),
      description: formValues.assignee.name,
      icon: <ProfileIcon width='24' height='24' />,
    },
    {
      title: t('Field'),
      description: (
        <MiniComponentContainer
          hasBorder
          className='text-12 font-semi-bold text-text-default'
        >
          {formValues.field.image && (
            <CircularBgImage imageSrc={formValues.field.image} />
          )}
          <span>{formValues.field.name}</span>
        </MiniComponentContainer>
      ),
      icon: <LocationIcon width='24' height='24' />,
    },
    {
      title: t('Task Name'),
      description: formValues.job.jobName,
      icon: <ScissorsIcon width='24' height='24' />,
    },

    {
      title: t('planned-date'),
      description: format(formValues.startDate, "dd/MM/yyyy'"),
      icon: <CalendarIcon width='24' height='24' />,
    },
    {
      title: t('productivity-target-per-worker'),
      description: `${formValues.averageTargetPerDay} ${formValues.measurementUnit.name}`,
      icon: <TargetIcon width='24' height='24' />,
    },
    {
      title: t('target-duration'),
      description: formValues.targetDuration.name,
      icon: <ClockIcon width='24' height='24' />,
    },
    {
      title: t('notes'),
      description: formValues?.note ? (
        <div title={formValues.note}>
          {truncateString({ text: formValues.note, length: 100 })}
        </div>
      ) : (
        '-'
      ),
      icon: <ClipboardIcon width='24' height='24' />,
    },
  ];

  const operationFormValidationSchema: yup.SchemaOf<IWorkingDay.IOperationFormValuesSchema> =
    yup.object().shape({
      assignee: nameIdPairValidationSchema(t),
      field: nameIdPairValidationSchema(t).shape({
        cropId: yup.number().required(t('this-field-is-required')),
        cropName: yup.string(),
        image: yup.string().nullable(),
      }),
      targetDuration: nameIdPairValidationSchema(t),
      measurementUnit: nameIdPairValidationSchema(t),
      job: yup.object().shape({
        jobId: yup.number().required(t('this-field-is-required')),
        jobName: yup.string().required(t('this-field-is-required')),
        notes: yup.string().nullable(),
        image: yup.string().nullable(),
        suppliers: yup
          .array()
          .of(nameIdPairValidationSchema(t))
          .nullable()
          .notRequired(),
      }),
      entityId: yup.number(),
      averageTargetPerDay: yup
        .number()
        .moreThan(0, t('This field must be greater than zero.'))
        .required(t('this-field-is-required')),
      startDate: yup.date().required(t('this-field-is-required')),
      note: yup
        .string()
        .notRequired()
        .max(
          500,
          t('this-field-not-accept-more-than-x-character', { count: 500 })
        ),
      recurring: yup.boolean().required(),
    });

  const getJobsListOptions = (
    data: IWorkingDay.IJobsResponseSchecma | undefined
  ) => {
    return [
      {
        label: t('custom-operation'),
        options: data?.customJobs ?? [],
      },
      {
        label: t('standard-operations'),
        options: data?.defaultJobs ?? [],
      },
    ];
  };

  return {
    openCreateTechnicalOperationModal,
    getFormSteps,
    getFormValues,
    getJobsListOptions,
    getGeneralCardItems,
    operationFormValidationSchema,
  };
};
